import { useMemo, useState } from "react";
import { formatDanishLongDate } from "@/util/date";
import { useAuthorshipsForReporting } from "@/hooks/useAuthorships";
import { useCourseCertificatesForReporting } from "@/hooks/useCourseCertificates";
import { useDispensationsForReporting } from "@/hooks/useDispensations";
import { useReductionsForReporting } from "@/hooks/useReductions";
import { useLegalAidsForReporting } from "@/hooks/useLegalAid";
import { useTeachingsForReporting } from "@/hooks/useTeachings";
import type { ReportingPeriod } from "@cle/models/reportingPeriod";
import { REQUIRED_PERIOD_LESSONS as REQUIRED_LESSONS } from "@cle/constants/lessonRequirements";
import type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";
import { usePendingIndberetningEvidence } from "@/hooks/useEfteruddannelseIndberetning";
import AwaitingEvidenceWarningModal from "@cle/components/AwaitingEvidenceWarningModal";
import { useAwaitingEvidence } from "@cle/hooks/useAwaitingEvidence";
import type { PeriodeLektioner } from "@/interfaces/efteruddannelse";
import {
  mapAuthorshipToEvidenceRow,
  mapCourseCertificateToEvidenceRow,
  mapDispensationToEvidenceRow,
  mapReductionToEvidenceRow,
  mapLegalAidToEvidenceRow,
  mapTeachingToEvidenceRow,
} from "@cle/utils/mapToSelectableEvidenceRow";
import { groupEvidenceItemIdsByCategory } from "@cle/utils/groupEvidenceItemIdsByCategory";

import {
  calculateReportCategorySummaries,
  getTotalDroppedLessons,
  getTotalSubmittedLessons,
  getTotalUsableLessons,
} from "@cle/utils/calculateReportCategorySummaries";
import {
  formatLabelList,
  getCategoryLimitNotice,
  getCategoryLimitWarning,
  getSurplusLessons,
  getSurplusNotice,
} from "@cle/utils/injunctionReportNotices";
import type { ReportRuleCategory } from "@cle/components/ReportRulesInfoContent";
import ReportInstructionPanel from "./components/ReportInstructionPanel";
import ReportPeriodProgressCard from "./components/ReportPeriodProgressCard";
import ReportSubmissionCard from "./components/ReportSubmissionCard";
import RoundedReportOverview, {
  type SelectableReportStepKey,
} from "./components/RoundedReportOverview";

const TOTAL_STEPS = 7;
const MISSING_VALUE = "-";

type OpenIndberetningDetailViewProps = {
  reportingPeriod: ReportingPeriod;
};

type ReportStepKey = SelectableReportStepKey | "overview";

type ReportStep = {
  description: string;
  key: ReportStepKey;
  shortLabel: string;
  title: string;
};

type ReportCategory = {
  canTransferLessons: boolean;
  documents: SelectableEvidenceRow[];
  label: string;
  /** Kategoriloft for det indberettede. Udeladt = ingen grænse (kursusdeltagelse). */
  maxReported?: number;
  /** Kategoriloft for det, der må overføres til næste periode. */
  maxTransfer: number;
};

type ReportCategorySummary = {
  selectedLessons: number;
  submittedLessons: number;
  totalLessons: number;
  transferredFromPreviousLessons: number;
  transferredToNext: number;
  droppedLessons: number;
};

type ReportCategorySummaries = Record<
  SelectableReportStepKey,
  ReportCategorySummary
>;

const REPORT_STEPS: ReportStep[] = [
  {
    key: "courseCertificates",
    shortLabel: "Kursusbevis",
    title: "Vælg kursusbeviser",
    description:
      "Her kan du se de kursusbeviser, der kan indgå i din aktuelle efteruddannelsesindberetning - Det er kun kursusbeviser, som er godkendt og ligger inden for perioden, der kan medtages.",
  },
  {
    key: "teaching",
    shortLabel: "Undervisning",
    title: "Vælg undervisning",
    description:
      "Markér de dokumenter for undervisning, du ønsker at medtage i perioden.",
  },
  {
    key: "authorship",
    shortLabel: "Forfatterskab",
    title: "Vælg forfatterskab",
    description:
      "Markér dokumentation for forfatterskab, der skal tælle med i indberetningen.",
  },
  {
    key: "reductions",
    shortLabel: "Reduktion",
    title: "Vælg reduktion",
    description:
      "Markér de reduktioner, du ønsker at bruge i denne efteruddannelsesperiode.",
  },
  {
    key: "legalAid",
    shortLabel: "Advokatvagt/retshjælp",
    title: "Vælg advokatvagt/retshjælp",
    description:
      "Markér dokumentation for advokatvagt eller retshjælp, du ønsker at medtage i perioden.",
  },
  {
    key: "dispensations",
    shortLabel: "Dispensation",
    title: "Dispensation",
    description:
      "Dispensationer for perioden medtages automatisk i indberetningen og kan ikke fravælges.",
  },
  {
    key: "overview",
    shortLabel: "Overblik",
    title: "Overblik over indberetning",
    description:
      "Gennemgå de valgte lektioner, før du indsender indberetningen.",
  },
];

const REPORT_CATEGORIES: Record<SelectableReportStepKey, ReportCategory> = {
  courseCertificates: {
    canTransferLessons: true,
    label: "Kursusdeltagelse",
    // Ingen maxReported: reglerne sætter ingen grænse for, hvor mange kursuslektioner
    // der må tælle med. Det er lektionskravet, der bremser — ikke kategorien.
    maxTransfer: 54,
    documents: [],
  },
  teaching: {
    canTransferLessons: true,
    label: "Undervisning",
    maxReported: 27,
    maxTransfer: 27,
    documents: [],
  },
  authorship: {
    canTransferLessons: true,
    label: "Forfatter",
    maxReported: 18,
    maxTransfer: 18,
    documents: [],
  },
  reductions: {
    canTransferLessons: false,
    label: "Reduktion",
    maxReported: 54,
    maxTransfer: 54,
    documents: [],
  },
  legalAid: {
    canTransferLessons: false,
    label: "Advokatvagt/retshjælp",
    maxReported: 18,
    maxTransfer: 18,
    documents: [],
  },
  dispensations: {
    canTransferLessons: false,
    label: "Dispensation",
    maxReported: 54,
    maxTransfer: 54,
    documents: [],
  },
};

// Drain-priority order for surplus above REQUIRED_LESSONS: transferable
// categories first (see calculateReportCategorySummaries).
const REPORT_CATEGORY_ORDER: SelectableReportStepKey[] = [
  "courseCertificates",
  "teaching",
  "authorship",
  "reductions",
  "legalAid",
  "dispensations",
];

// Kategorier, hvis overskud ikke kan overføres, og derfor bortfalder, når der
// allerede er indberettet REQUIRED_LESSONS lektioner.
const NON_TRANSFERABLE_CATEGORY_LABELS = formatLabelList(
  REPORT_CATEGORY_ORDER.filter(
    (key) => !REPORT_CATEGORIES[key].canTransferLessons,
  ).map((key) => REPORT_CATEGORIES[key].label),
);

const mapCarryInToCategoryLessons = (
  carryIn: PeriodeLektioner | null,
): Record<SelectableReportStepKey, number> => ({
  courseCertificates: carryIn?.kursus ?? 0,
  teaching: carryIn?.undervisning ?? 0,
  authorship: carryIn?.forfatter ?? 0,
  reductions: carryIn?.reduktion ?? 0,
  legalAid: carryIn?.advokatvagt ?? 0,
  dispensations: carryIn?.dispensation ?? 0,
});

const createInitialSelectedEvidenceItems = () => {
  return {
    courseCertificates: [],
    teaching: [],
    authorship: [],
    reductions: [],
    legalAid: [],
    dispensations: [],
  };
};

const getSelectedLessons = (
  documents: SelectableEvidenceRow[],
  selectedIds: string[],
) => {
  return documents
    .filter((document) => selectedIds.includes(document.id))
    .reduce((total, document) => total + document.lessons, 0);
};

const formatPeriodRange = (reportingPeriod: ReportingPeriod) => {
  return `${formatDanishLongDate(
    reportingPeriod.periodFromDate,
    MISSING_VALUE,
  )} - ${formatDanishLongDate(
    reportingPeriod.periodToDateMinusOneDay,
    MISSING_VALUE,
  )}`;
};

const createPeriodeLektioner = (
  categorySummaries: ReportCategorySummaries,
  getLessons: (summary: ReportCategorySummary) => number,
): PeriodeLektioner => {
  const lessons = {
    advokatvagt: getLessons(categorySummaries.legalAid),
    dispensation: getLessons(categorySummaries.dispensations),
    forfatter: getLessons(categorySummaries.authorship),
    kursus: getLessons(categorySummaries.courseCertificates),
    reduktion: getLessons(categorySummaries.reductions),
    undervisning: getLessons(categorySummaries.teaching),
  };

  return {
    ...lessons,
    sum: Object.values(lessons).reduce((total, lesson) => total + lesson, 0),
  };
};

const getSubmissionErrorMessage = (
  stepKey: SelectableReportStepKey,
): string => {
  const errorMessages: Record<SelectableReportStepKey, string> = {
    authorship: "Forfatterskab kunne ikke indlæses lige nu.",
    courseCertificates: "Kursusbeviser kunne ikke indlæses lige nu.",
    dispensations: "Dispensationer kunne ikke indlæses lige nu.",
    reductions: "Reduktioner kunne ikke indlæses lige nu.",
    legalAid: "Advokatvagt/retshjælp kunne ikke indlæses lige nu.",
    teaching: "Undervisning kunne ikke indlæses lige nu.",
  };

  return errorMessages[stepKey];
};

/**
 * Kategorierne, som hjælpeteksten beskriver, udledt af REPORT_CATEGORIES i
 * stedet for skrevet af igen. Rækkefølgen er den, brugeren møder i trinnene.
 */
const REPORT_RULE_CATEGORIES: ReportRuleCategory[] = REPORT_CATEGORY_ORDER.map(
  (key) => ({
    label: REPORT_CATEGORIES[key].label,
    maxReported: REPORT_CATEGORIES[key].maxReported,
    canTransferLessons: REPORT_CATEGORIES[key].canTransferLessons,
  }),
);

const OpenIndberetningDetailView = ({
  reportingPeriod,
}: OpenIndberetningDetailViewProps) => {
  const periodId = reportingPeriod.id;
  const openReportId = reportingPeriod.openReportId;
  const { data: pendingEvidence, isLoading: isPendingEvidenceLoading } =
    usePendingIndberetningEvidence(openReportId);
  const { data: awaitingEvidence } = useAwaitingEvidence({
    kind: "period",
    id: periodId,
  });
  const {
    data: reportableCourseCertificates = [],
    isError: isCourseCertificatesError,
    isLoading: isCourseCertificatesLoading,
  } = useCourseCertificatesForReporting(periodId);
  const {
    data: reportableReductions = [],
    isError: isReductionsError,
    isLoading: isReductionsLoading,
  } = useReductionsForReporting(periodId);
  const {
    data: reportableLegalAids = [],
    isError: isLegalAidsError,
    isLoading: isLegalAidsLoading,
  } = useLegalAidsForReporting(periodId);
  const {
    data: reportableTeachings = [],
    isError: isTeachingsError,
    isLoading: isTeachingsLoading,
  } = useTeachingsForReporting(periodId);
  const {
    data: reportableAuthorships = [],
    isError: isAuthorshipsError,
    isLoading: isAuthorshipsLoading,
  } = useAuthorshipsForReporting(periodId);
  const {
    data: reportableDispensations = [],
    isError: isDispensationsError,
    isLoading: isDispensationsLoading,
  } = useDispensationsForReporting(periodId);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  /** Kun brugerens egne ændringer. Er den null, vises udvalget udledt af data
   * (seededSelection), så vi ikke skal spejle async data ind i state. */
  const [userSelection, setUserSelection] = useState<Record<
    SelectableReportStepKey,
    string[]
  > | null>(null);

  const reportingPeriodRange = formatPeriodRange(reportingPeriod);
  const reportingDeadline = formatDanishLongDate(
    reportingPeriod.deadline,
    "Ingen frist",
  );
  const courseCertificateDocuments = useMemo(
    () =>
      reportableCourseCertificates.map((certificate) =>
        mapCourseCertificateToEvidenceRow(certificate),
      ),
    [reportableCourseCertificates],
  );
  const reductionDocuments = useMemo(
    () => reportableReductions.map(mapReductionToEvidenceRow),
    [reportableReductions],
  );
  const legalAidDocuments = useMemo(
    () => reportableLegalAids.map(mapLegalAidToEvidenceRow),
    [reportableLegalAids],
  );
  const teachingDocuments = useMemo(
    () => reportableTeachings.map(mapTeachingToEvidenceRow),
    [reportableTeachings],
  );
  const authorshipDocuments = useMemo(
    () => reportableAuthorships.map(mapAuthorshipToEvidenceRow),
    [reportableAuthorships],
  );
  const dispensationDocuments = useMemo(
    () => reportableDispensations.map(mapDispensationToEvidenceRow),
    [reportableDispensations],
  );
  const allDispensationIds = useMemo(
    () => dispensationDocuments.map((document) => document.id),
    [dispensationDocuments],
  );
  const documentsByCategory = useMemo(
    () => ({
      courseCertificates: courseCertificateDocuments,
      teaching: teachingDocuments,
      authorship: authorshipDocuments,
      reductions: reductionDocuments,
      legalAid: legalAidDocuments,
      dispensations: dispensationDocuments,
    }),
    [
      authorshipDocuments,
      courseCertificateDocuments,
      dispensationDocuments,
      legalAidDocuments,
      reductionDocuments,
      teachingDocuments,
    ],
  );
  // Udgangspunktet udledt af data: genoptager et gemt udkast, ellers tomt.
  // null indtil alle kilder er indlæst, så vi ikke seeder på halve data.
  const seededSelection = useMemo(() => {
    if (
      isCourseCertificatesLoading ||
      isReductionsLoading ||
      isLegalAidsLoading ||
      isDispensationsLoading ||
      isTeachingsLoading ||
      isAuthorshipsLoading ||
      (openReportId && isPendingEvidenceLoading)
    ) {
      return null;
    }

    if (openReportId && pendingEvidence?.evidenceItemIds.length) {
      return groupEvidenceItemIdsByCategory(
        pendingEvidence.evidenceItemIds,
        documentsByCategory,
      );
    }

    return createInitialSelectedEvidenceItems();
  }, [
    documentsByCategory,
    isAuthorshipsLoading,
    isCourseCertificatesLoading,
    isDispensationsLoading,
    isLegalAidsLoading,
    isPendingEvidenceLoading,
    isReductionsLoading,
    isTeachingsLoading,
    openReportId,
    pendingEvidence,
  ]);

  // Dispensationer følger altid data og kan ikke afvælges, så de påtvinges her
  // i stedet for at blive holdt synkroniseret i en effekt.
  const selectedEvidenceItemIds = useMemo(
    () => ({
      ...(userSelection ??
        seededSelection ??
        createInitialSelectedEvidenceItems()),
      dispensations: allDispensationIds,
    }),
    [allDispensationIds, seededSelection, userSelection],
  );

  const reportCategories = useMemo(
    () => ({
      ...REPORT_CATEGORIES,
      courseCertificates: {
        ...REPORT_CATEGORIES.courseCertificates,
        documents: courseCertificateDocuments,
      },
      teaching: {
        ...REPORT_CATEGORIES.teaching,
        documents: teachingDocuments,
      },
      authorship: {
        ...REPORT_CATEGORIES.authorship,
        documents: authorshipDocuments,
      },
      reductions: {
        ...REPORT_CATEGORIES.reductions,
        documents: reductionDocuments,
      },
      legalAid: {
        ...REPORT_CATEGORIES.legalAid,
        documents: legalAidDocuments,
      },
      dispensations: {
        ...REPORT_CATEGORIES.dispensations,
        documents: dispensationDocuments,
      },
    }),
    [
      authorshipDocuments,
      courseCertificateDocuments,
      dispensationDocuments,
      legalAidDocuments,
      reductionDocuments,
      teachingDocuments,
    ],
  );
  const currentStep = REPORT_STEPS[currentStepIndex];
  const currentSelectableStep =
    currentStep.key === "overview" ? null : currentStep.key;
  const currentCategory = currentSelectableStep
    ? reportCategories[currentSelectableStep]
    : null;

  const selectedLessonsByCategory = useMemo(
    () =>
      Object.entries(reportCategories).reduce(
        (lessonTotals, [key, category]) => {
          const categoryKey = key as SelectableReportStepKey;

          return {
            ...lessonTotals,
            [categoryKey]: getSelectedLessons(
              category.documents,
              selectedEvidenceItemIds[categoryKey],
            ),
          };
        },
        {} as Record<SelectableReportStepKey, number>,
      ),
    [reportCategories, selectedEvidenceItemIds],
  );

  const carryInByCategory = useMemo(
    () =>
      mapCarryInToCategoryLessons(
        reportingPeriod.transferredFromPreviousLessons,
      ),
    [reportingPeriod.transferredFromPreviousLessons],
  );

  const categorySummaries = useMemo(
    () =>
      calculateReportCategorySummaries(
        REPORT_CATEGORY_ORDER.map((categoryKey) => ({
          key: categoryKey,
          canTransferLessons: reportCategories[categoryKey].canTransferLessons,
          maxReported: reportCategories[categoryKey].maxReported,
          maxTransfer: reportCategories[categoryKey].maxTransfer,
          selectedLessons: selectedLessonsByCategory[categoryKey],
          transferredFromPreviousLessons: carryInByCategory[categoryKey],
        })),
        REQUIRED_LESSONS,
        // Backenden afviser enhver overførsel, hvis der er indberettet under 54 lektioner.
        { requireFullReportBeforeTransfer: true },
      ).reduce(
        (summaryByCategory, summary) => ({
          ...summaryByCategory,
          [summary.key]: {
            selectedLessons: summary.selectedLessons,
            submittedLessons: summary.submittedLessons,
            totalLessons: summary.totalLessons,
            transferredFromPreviousLessons:
              summary.transferredFromPreviousLessons,
            transferredToNext: summary.transferredToNext,
            droppedLessons: summary.droppedLessons,
          },
        }),
        {} as ReportCategorySummaries,
      ),
    [carryInByCategory, reportCategories, selectedLessonsByCategory],
  );

  const countedLessons = getTotalSubmittedLessons(
    Object.values(categorySummaries),
  );
  const missingLessons = Math.max(REQUIRED_LESSONS - countedLessons, 0);
  // Overskriften tæller kun lektioner, der bliver til noget — indberettet nu eller
  // overført til næste periode. Det, der bortfalder, står for sig.
  // Kun overskriften: submit-dialogen skal blive på countedLessons, ellers passer
  // "du har valgt X af 54, der mangler Y" ikke sammen.
  const usableLessons = getTotalUsableLessons(Object.values(categorySummaries));
  const droppedLessons = getTotalDroppedLessons(
    Object.values(categorySummaries),
  );
  // Kategoriloftet er ikke laengere en spaerre i selektionen: man maa vaelge mere end
  // loftet, og overskuddet bortfalder synligt i stedet.
  const currentCategoryLimit =
    currentSelectableStep !== null
      ? {
          ...categorySummaries[currentSelectableStep],
          maxReported: reportCategories[currentSelectableStep].maxReported,
        }
      : null;

  const reportedLessons = useMemo(
    () =>
      createPeriodeLektioner(
        categorySummaries,
        (summary) => summary.submittedLessons,
      ),
    [categorySummaries],
  );
  const transferredToNextLessons = useMemo(
    () =>
      createPeriodeLektioner(
        categorySummaries,
        (summary) => summary.transferredToNext,
      ),
    [categorySummaries],
  );

  const handleToggleEvidenceItem = (evidenceItemId: string) => {
    if (!currentSelectableStep || currentSelectableStep === "dispensations") {
      return;
    }

    setUserSelection(() => {
      const currentStepIds = selectedEvidenceItemIds[currentSelectableStep];

      if (currentStepIds.includes(evidenceItemId)) {
        return {
          ...selectedEvidenceItemIds,
          [currentSelectableStep]: currentStepIds.filter(
            (id) => id !== evidenceItemId,
          ),
        };
      }

      return {
        ...selectedEvidenceItemIds,
        [currentSelectableStep]: [...currentStepIds, evidenceItemId],
      };
    });
  };

  const handleSelectEvidenceItems = (evidenceItemIds: string[]) => {
    if (!currentSelectableStep || currentSelectableStep === "dispensations") {
      return;
    }

    setUserSelection({
      ...selectedEvidenceItemIds,
      [currentSelectableStep]: evidenceItemIds,
    });
  };

  const handlePreviousStep = () => {
    setCurrentStepIndex((stepIndex) => Math.max(stepIndex - 1, 0));
  };

  const handleNextStep = () => {
    setCurrentStepIndex((stepIndex) =>
      Math.min(stepIndex + 1, REPORT_STEPS.length - 1),
    );
  };

  // resolveIndberetningDetailVariant sender kun hertil med en åben kladde, men typen bærer
  // ikke den garanti. Uden id kan der ikke indsendes, så vis det frem for at lade brugeren
  // gå gennem hele flowet og ramme en fejl til sidst.
  if (!openReportId) {
    return (
      <div className="cle-report-detail">
        <div className="alert alert-info mb-0" role="status">
          Der er ingen åben indberetning for denne efteruddannelsesperiode.
        </div>
      </div>
    );
  }

  return (
    <div className="cle-report-detail">
      <AwaitingEvidenceWarningModal items={awaitingEvidence} />
      <section className="cle-report-detail__reporting-shell">
        <header className="cle-report-detail__page-header">
          <div>
            <h1 className="cle-report-detail__page-title mb-0">
              Efteruddannelse for perioden {reportingPeriodRange}
            </h1>
            <p className="text-muted small mb-0">
              Du er ved at indberette for denne efteruddannelsesperiode.
            </p>
          </div>
          <p className="cle-report-detail__deadline mb-0">
            Frist {reportingDeadline}
          </p>
        </header>

        <div className="cle-report-detail__reporting-body">
          <ReportInstructionPanel
            awaitingEvidence={awaitingEvidence}
            currentStep={currentStep}
            currentStepIndex={currentStepIndex}
            missingLessons={missingLessons}
            onNextStep={handleNextStep}
            onPreviousStep={handlePreviousStep}
            openReportId={openReportId}
            requiredLessons={REQUIRED_LESSONS}
            reportedLessons={reportedLessons}
            reportingPeriodId={reportingPeriod.id}
            selectedEvidenceItemIds={selectedEvidenceItemIds}
            selectedLessons={countedLessons}
            totalSteps={TOTAL_STEPS}
            transferredToNextLessons={transferredToNextLessons}
            ruleCategories={REPORT_RULE_CATEGORIES}
          />

          <div className="cle-report-detail__workspace">
            <ReportPeriodProgressCard
              deadline={reportingDeadline}
              missingLessons={missingLessons}
              periodRange={reportingPeriodRange}
              droppedLessons={droppedLessons}
              requiredLessons={REQUIRED_LESSONS}
              selectedLessons={usableLessons}
              transferredInLessons={
                reportingPeriod.transferredFromPreviousPeriod ?? 0
              }
            />

            {currentCategory && currentSelectableStep ? (
              <ReportSubmissionCard
                description={
                  currentSelectableStep === "dispensations"
                    ? "Dispensationer for perioden medtages automatisk i indberetningen."
                    : `Vælg de dokumenter, der skal tælle med som ${currentCategory.label.toLowerCase()}.`
                }
                disableReportedSelection={false}
                documents={currentCategory.documents}
                errorMessage={getSubmissionErrorMessage(currentSelectableStep)}
                maxLessons={currentCategoryLimit?.maxReported}
                noticeMessage={getCategoryLimitNotice(currentCategoryLimit)}
                warningMessage={getCategoryLimitWarning(currentCategoryLimit)}
                evidenceStepKey={currentSelectableStep}
                isError={
                  (currentSelectableStep === "courseCertificates" &&
                    isCourseCertificatesError) ||
                  (currentSelectableStep === "reductions" &&
                    isReductionsError) ||
                  (currentSelectableStep === "legalAid" && isLegalAidsError) ||
                  (currentSelectableStep === "dispensations" &&
                    isDispensationsError) ||
                  (currentSelectableStep === "teaching" && isTeachingsError) ||
                  (currentSelectableStep === "authorship" && isAuthorshipsError)
                }
                isLoading={
                  (currentSelectableStep === "courseCertificates" &&
                    isCourseCertificatesLoading) ||
                  (currentSelectableStep === "reductions" &&
                    isReductionsLoading) ||
                  (currentSelectableStep === "legalAid" &&
                    isLegalAidsLoading) ||
                  (currentSelectableStep === "dispensations" &&
                    isDispensationsLoading) ||
                  (currentSelectableStep === "teaching" &&
                    isTeachingsLoading) ||
                  (currentSelectableStep === "authorship" &&
                    isAuthorshipsLoading)
                }
                onSelectEvidenceItems={handleSelectEvidenceItems}
                onToggleEvidenceItem={handleToggleEvidenceItem}
                selectedEvidenceItemIds={
                  selectedEvidenceItemIds[currentSelectableStep]
                }
                selectedLessons={
                  categorySummaries[currentSelectableStep].selectedLessons
                }
                selectionLocked={currentSelectableStep === "dispensations"}
                showTransferredFromPrevious={
                  currentSelectableStep !== "dispensations"
                }
                title={currentStep.shortLabel}
                transferredFromPreviousLessons={
                  categorySummaries[currentSelectableStep]
                    .transferredFromPreviousLessons
                }
              />
            ) : (
              <RoundedReportOverview
                categorySummaries={categorySummaries}
                reportCategories={reportCategories}
                footnote={
                  getSurplusNotice(
                    getSurplusLessons(countedLessons, REQUIRED_LESSONS),
                    REQUIRED_LESSONS,
                    NON_TRANSFERABLE_CATEGORY_LABELS,
                  ) ?? undefined
                }
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpenIndberetningDetailView;
